import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Slice } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import { normalizeImportedDocxHtml } from '../../../../../utils/docxListNormalize';

/**
 * ClipboardClean
 * --------------
 * Normalizes HTML pasted from Microsoft Word / Excel / Google Docs so the
 * formatting (fonts, sizes, colours, highlight, alignment incl. justify,
 * paragraph spacing, indents, numbered / bulleted lists, table borders and
 * cell shading) is preserved while stripping Office-specific cruft that
 * ProseMirror cannot use.
 *
 * Word delivers most of its formatting through a `<style>` block (e.g. the
 * Normal style's font, size and spacing live in `p.MsoNormal {…}`) and its
 * lists as plain paragraphs tagged `mso-list:l0 level1`. Tiptap keeps neither,
 * so we inline the style rules onto the elements, rebuild real `<ol>/<ul>`
 * lists and convert values into forms the editor schema and the backend
 * sanitizer understand.
 */

function isWordHTML(html: string): boolean {
  const h = html.toLowerCase();
  return (
    h.includes('mso-') ||
    h.includes('class="msonormal') ||
    h.includes('class=msonormal') ||
    h.includes('xmlns:w=') ||
    h.includes('urn:schemas-microsoft-com:office:word')
  );
}

function isExcelHTML(html: string): boolean {
  const h = html.toLowerCase();
  return (
    h.includes('xmlns:x=') ||
    h.includes('urn:schemas-microsoft-com:office:excel') ||
    /class="?xl\d+/i.test(h)
  );
}

function isOfficeHTML(html: string): boolean {
  return isWordHTML(html) || isExcelHTML(html) || /<!--\s*StartFragment\s*-->/i.test(html);
}

// ---------------------------------------------------------------------------
// Inline-style helpers
// ---------------------------------------------------------------------------

type Decls = Map<string, string>;

function parseDecls(style: string | null | undefined): Decls {
  const map: Decls = new Map();
  if (!style) return map;
  for (const part of style.split(';')) {
    const idx = part.indexOf(':');
    if (idx === -1) continue;
    const key = part.slice(0, idx).trim().toLowerCase();
    const value = part.slice(idx + 1).replace(/\s+/g, ' ').trim();
    if (!key || !value) continue;
    // Later declarations win, like the CSS cascade inside one style attribute.
    map.delete(key);
    map.set(key, value);
  }
  return map;
}

function writeDecls(el: Element, decls: Decls) {
  const css = Array.from(decls.entries())
    .map(([k, v]) => `${k}: ${v}`)
    .join('; ');
  if (css) el.setAttribute('style', css);
  else el.removeAttribute('style');
}

const UNIT_TO_PT: Record<string, number> = {
  pt: 1,
  in: 72,
  cm: 72 / 2.54,
  mm: 72 / 25.4,
  px: 0.75,
  pc: 12
};

/** Convert a CSS length (`.5in`, `1.27cm`, `6.0pt`, `-18pt`, `0`) to points
 *  with a leading digit (`36pt`). Returns null for anything else. */
function toPt(value: string | undefined, allowNegative = false): string | null {
  if (value == null) return null;
  const v = value.trim().toLowerCase();
  if (v === '0' || v === 'auto') return v === '0' ? '0pt' : null;
  const m = /^(-?)(\d*\.?\d+)(pt|in|cm|mm|px|pc)$/.exec(v);
  if (!m) return null;
  const num = parseFloat(m[2]) * UNIT_TO_PT[m[3]];
  if (!Number.isFinite(num)) return null;
  const rounded = Math.round(num * 100) / 100;
  if (rounded === 0) return '0pt';
  if (m[1] === '-' && !allowNegative) return null;
  return `${m[1]}${rounded}pt`;
}

/** Expand `margin` / `padding` shorthands into their four longhands. */
function expandBoxShorthand(decls: Decls, prop: 'margin' | 'padding') {
  const short = decls.get(prop);
  if (short == null) return;
  const parts = short.split(/\s+/).filter(Boolean);
  if (parts.length >= 1 && parts.length <= 4) {
    const [t, r = t, b = t, l = r] = parts;
    const next: Decls = new Map();
    // Re-insert in place so later longhands still override the shorthand.
    for (const [k, v] of decls) {
      if (k === prop) {
        next.set(`${prop}-top`, t);
        next.set(`${prop}-right`, r);
        next.set(`${prop}-bottom`, b);
        next.set(`${prop}-left`, l);
      } else {
        next.delete(k);
        next.set(k, v);
      }
    }
    decls.clear();
    next.forEach((v, k) => decls.set(k, v));
  } else {
    decls.delete(prop);
  }
}

/** Word font names → a stack with sensible fallbacks (Aptos is a cloud font
 *  most browsers don't have; Carlito is the bundled Calibri substitute). */
function normalizeFontFamily(value: string): string {
  const first = value.split(',')[0].replace(/["']/g, '').trim().toLowerCase();
  if (first === 'aptos' || first === 'aptos display') return 'Aptos, Calibri, Carlito, "Segoe UI", sans-serif';
  if (first === 'calibri') return 'Calibri, Carlito, sans-serif';
  if (first === 'calibri light') return '"Calibri Light", Calibri, Carlito, sans-serif';
  if (first === 'cambria') return 'Cambria, Georgia, serif';
  return value;
}

/** First colour token of a `background` shorthand / highlight value. */
function pickColor(value: string | undefined): string | null {
  if (!value) return null;
  const v = value.trim();
  const fn = /^(rgba?\([^)]*\)|hsla?\([^)]*\))/i.exec(v);
  const token = fn ? fn[1] : v.split(/\s+/)[0];
  if (!token || /^(transparent|none|inherit|initial|unset|auto|windowtext|url\()/i.test(token)) return null;
  if (!/^(#[0-9a-f]{3,8}|rgba?\(.*\)|hsla?\(.*\)|[a-z]+)$/i.test(token)) return null;
  return token;
}

// ---------------------------------------------------------------------------
// 1. <style> block → inline styles
// ---------------------------------------------------------------------------

interface ListLevelDef {
  format?: string;
  text?: string;
}

/** Reads every `<style>` block, inlines its rules onto matching elements and
 *  returns Word's `@list lN:levelM` definitions (used to classify lists).
 *  Inline styles keep priority over class rules, as in CSS. */
function inlineStyleBlocks(doc: Document, root: HTMLElement): Map<string, ListLevelDef> {
  const listDefs = new Map<string, ListLevelDef>();
  const pending = new Map<Element, string[]>();

  for (const block of Array.from(doc.querySelectorAll('style'))) {
    // Word wraps the CSS in <!-- --> and sprinkles /* comments */ before
    // selectors; left in place they turned `p.MsoNormal` into an invalid
    // selector, so the Normal style's font/size/spacing never applied.
    const css = (block.textContent || '')
      .replace(/<!--|-->/g, ' ')
      .replace(/\/\*[\s\S]*?\*\//g, ' ');
    const ruleRe = /([^{}]+)\{([^{}]*)\}/g;
    let m: RegExpExecArray | null;
    while ((m = ruleRe.exec(css))) {
      const selectorList = m[1].trim();
      const declText = m[2].trim();
      if (!selectorList || !declText) continue;
      if (selectorList.startsWith('@')) {
        const lm = /^@list\s+(l\d+):level(\d+)$/i.exec(selectorList);
        if (lm) {
          const d = parseDecls(declText);
          listDefs.set(`${lm[1].toLowerCase()}:${lm[2]}`, {
            format: d.get('mso-level-number-format')?.toLowerCase(),
            text: d.get('mso-level-text')
          });
        }
        continue;
      }
      for (const raw of selectorList.split(',')) {
        const sel = raw.trim();
        if (!sel || /::|:hover|:focus|:active|:visited|:link/.test(sel)) continue;
        let matches: NodeListOf<Element>;
        try {
          matches = root.querySelectorAll(sel);
        } catch {
          continue;
        }
        matches.forEach((el) => {
          const list = pending.get(el) || [];
          list.push(declText);
          pending.set(el, list);
        });
      }
    }
    block.parentNode?.removeChild(block);
  }

  pending.forEach((rules, el) => {
    const inline = el.getAttribute('style') || '';
    el.setAttribute('style', [...rules, inline].filter(Boolean).join('; '));
  });
  return listDefs;
}

// ---------------------------------------------------------------------------
// 2. Word list paragraphs → real <ol>/<ul>
// ---------------------------------------------------------------------------

const MSO_LIST_RE = /mso-list\s*:\s*(l\d+)\s+level(\d+)/i;

interface ListInfo {
  tag: 'ol' | 'ul';
  style: string;
  start: number;
  level: number;
  key: string;
}

function romanToInt(s: string): number {
  const map: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
  let total = 0;
  const lower = s.toLowerCase();
  for (let i = 0; i < lower.length; i++) {
    const cur = map[lower[i]] || 0;
    const next = map[lower[i + 1]] || 0;
    total += cur < next ? -cur : cur;
  }
  return total;
}

function alphaToInt(s: string): number {
  let n = 0;
  for (const ch of s.toLowerCase()) n = n * 26 + (ch.charCodeAt(0) - 96);
  return n;
}

function bulletStyleFor(marker: string, font: string): string {
  const f = font.toLowerCase();
  if (marker === 'o' && /courier/.test(f)) return 'circle';
  if (/wingdings/.test(f)) {
    if (marker === '§' || marker === 'q' || marker === 'n') return 'square';
    if (marker === 'Ø' || marker === 'Ø' || marker === 'è' || marker === 'ð') return 'arrow';
    if (marker === 'ü' || marker === 'þ') return 'check';
    return 'disc';
  }
  if (/^[-–—]$/.test(marker)) return 'dash';
  if (/^[▪■□]$/.test(marker)) return 'square';
  if (/^[◦○o]$/.test(marker)) return 'circle';
  if (/^[➢►▶>»]$/.test(marker)) return 'arrow';
  if (/^[✓✔]$/.test(marker)) return 'check';
  return 'disc';
}

function findMarkerSpan(p: HTMLElement): HTMLElement | null {
  return (
    Array.from(p.querySelectorAll<HTMLElement>('span')).find((s) =>
      /mso-list\s*:\s*ignore/i.test(s.getAttribute('style') || '')
    ) || null
  );
}

/** Topmost wrapper of the marker span that is a direct child of the paragraph. */
function markerWrapper(p: HTMLElement, marker: HTMLElement): HTMLElement {
  let node: HTMLElement = marker;
  while (node.parentElement && node.parentElement !== p) node = node.parentElement;
  return node;
}

function classifyListParagraph(p: HTMLElement, listDefs: Map<string, ListLevelDef>): ListInfo | null {
  const lm = MSO_LIST_RE.exec(p.getAttribute('style') || '');
  if (!lm) return null;
  const listId = lm[1].toLowerCase();
  const level = Math.max(1, parseInt(lm[2], 10) || 1);
  const def = listDefs.get(`${listId}:${level}`);

  const markerSpan = findMarkerSpan(p);
  if (!markerSpan) return null;
  const marker = (markerSpan.textContent || '').replace(/[\s\u00a0]+/g, '');
  const wrapper = markerWrapper(p, markerSpan);
  const fontSource = `${wrapper.getAttribute('style') || ''};${markerSpan.getAttribute('style') || ''}`;
  const font = /font-family\s*:\s*([^;]+)/i.exec(fontSource)?.[1] || '';
  const format = def?.format || '';

  const isBullet =
    format === 'bullet' ||
    /symbol|wingdings|webdings/i.test(font) ||
    (marker === 'o' && /courier/i.test(font)) ||
    (marker.length > 0 && !/[0-9a-z]/i.test(marker));
  if (isBullet) {
    const style = bulletStyleFor(marker, font);
    return { tag: 'ul', style, start: 1, level, key: `ul:${style}:${listId}` };
  }

  const m = /^(\()?([0-9]+|[a-z]+|[A-Z]+)([.)])?$/.exec(marker);
  if (!m) return null; // e.g. "1.1.", "Article 1" — keep the literal text
  const open = !!m[1];
  const body = m[2];
  const close = m[3] || '';
  let suffix: '' | '-paren' | '-parens';
  if (open && close === ')') suffix = '-parens';
  else if (!open && close === ')') suffix = '-paren';
  else if (!open && close === '.') suffix = '';
  else return null;

  let kind: string;
  let start: number;
  if (/^\d+$/.test(body)) {
    kind = 'decimal';
    start = parseInt(body, 10);
  } else {
    const isRomanFormat = format.startsWith('roman');
    const isAlphaFormat = format.startsWith('alpha');
    const looksRoman = /^[ivxlcdm]+$/i.test(body) && (body.length > 1 || /^i$/i.test(body));
    const roman = isRomanFormat || (!isAlphaFormat && looksRoman);
    const upper = body === body.toUpperCase();
    kind = `${upper ? 'upper' : 'lower'}-${roman ? 'roman' : 'alpha'}`;
    start = roman ? romanToInt(body) : alphaToInt(body);
  }
  const style = `${kind}${suffix}`;
  return { tag: 'ol', style, start: Math.max(1, start || 1), level, key: `ol:${style}:${listId}` };
}

function isSkippableBetween(node: Node | null): boolean {
  if (!node) return false;
  if (node.nodeType === Node.COMMENT_NODE) return true;
  return node.nodeType === Node.TEXT_NODE && !(node.textContent || '').trim();
}

function nextSignificantSibling(node: Node): Node | null {
  let n = node.nextSibling;
  while (n && isSkippableBetween(n)) n = n.nextSibling;
  return n;
}

const LIST_PARAGRAPH_DROP = new Set(['margin-left', 'margin-right', 'margin-top', 'margin-bottom', 'text-indent', 'mso-list', 'margin']);

function convertWordLists(root: HTMLElement, listDefs: Map<string, ListLevelDef>) {
  const candidates = Array.from(root.querySelectorAll<HTMLElement>('p, h1, h2, h3, h4, h5, h6')).filter((el) =>
    MSO_LIST_RE.test(el.getAttribute('style') || '')
  );
  if (!candidates.length) return;
  const candidateSet = new Set<Node>(candidates);
  const done = new Set<Node>();

  for (const first of candidates) {
    if (done.has(first)) continue;
    const run: HTMLElement[] = [first];
    done.add(first);
    let n = nextSignificantSibling(first);
    while (n && candidateSet.has(n)) {
      run.push(n as HTMLElement);
      done.add(n);
      n = nextSignificantSibling(n);
    }

    const doc = first.ownerDocument;
    const anchor = doc.createComment('list-anchor');
    first.parentNode?.insertBefore(anchor, first);
    const stack: { level: number; list: HTMLElement; key: string }[] = [];

    for (const p of run) {
      const info = classifyListParagraph(p, listDefs);
      if (!info) {
        // Unsupported marker format: keep the paragraph (and its literal
        // number) with Word's hanging indent instead of guessing.
        stack.length = 0;
        anchor.parentNode?.insertBefore(p, anchor);
        const decls = parseDecls(p.getAttribute('style'));
        decls.delete('mso-list');
        writeDecls(p, decls);
        continue;
      }

      const markerSpan = findMarkerSpan(p);
      if (markerSpan) markerWrapper(p, markerSpan).remove();
      Array.from(p.childNodes).forEach((c) => {
        if (c.nodeType === Node.COMMENT_NODE) c.remove();
      });
      const decls = parseDecls(p.getAttribute('style'));
      LIST_PARAGRAPH_DROP.forEach((k) => decls.delete(k));
      writeDecls(p, decls);

      while (stack.length && stack[stack.length - 1].level > info.level) stack.pop();
      let top = stack[stack.length - 1];
      if (top && top.level === info.level && top.key !== info.key) {
        stack.pop();
        top = stack[stack.length - 1];
      }
      if (!top || top.level < info.level) {
        const list = doc.createElement(info.tag);
        list.setAttribute('data-list-style', info.style);
        // An explicit start also stops normalizeImportedDocxHtml from merging
        // this list into a neighbouring list with a different numbering.
        if (info.tag === 'ol') list.setAttribute('start', String(info.start));
        if (top) {
          let parentLi = top.list.lastElementChild as HTMLElement | null;
          if (!parentLi) {
            parentLi = doc.createElement('li');
            top.list.appendChild(parentLi);
          }
          parentLi.appendChild(list);
        } else {
          anchor.parentNode?.insertBefore(list, anchor);
        }
        stack.push({ level: info.level, list, key: info.key });
        top = stack[stack.length - 1];
      }
      const li = doc.createElement('li');
      li.appendChild(p);
      top.list.appendChild(li);
    }
    anchor.remove();
  }
}

// ---------------------------------------------------------------------------
// 3. Text highlight / paragraph-level character formatting
// ---------------------------------------------------------------------------

/** Word marks highlighted text as `<span style="background:yellow;
 *  mso-highlight:yellow">`; the editor's Highlight mark only reads `<mark>`. */
function highlightToMark(root: HTMLElement) {
  const doc = root.ownerDocument;
  root.querySelectorAll<HTMLElement>('span, font, b, strong, i, em, u, s, a').forEach((el) => {
    const decls = parseDecls(el.getAttribute('style'));
    const color =
      pickColor(decls.get('mso-highlight')) ||
      pickColor(decls.get('background-color')) ||
      pickColor(decls.get('background'));
    decls.delete('mso-highlight');
    decls.delete('background');
    decls.delete('background-color');
    if (!color) return;
    writeDecls(el, decls);
    if (!(el.textContent || '').replace(/\u00a0/g, ' ').trim()) return;
    const mark = doc.createElement('mark');
    mark.setAttribute('data-color', color);
    while (el.firstChild) mark.appendChild(el.firstChild);
    el.appendChild(mark);
  });
}

const CHAR_PROPS = ['font-family', 'font-size', 'color', 'font-weight', 'font-style'];

/** Character formatting set on the paragraph itself (usually inherited from
 *  the Word style, e.g. Normal = Aptos 12pt) is moved onto a wrapping span so
 *  the editor's text-style marks pick it up. */
function paragraphFontToSpan(root: HTMLElement) {
  const doc = root.ownerDocument;
  root.querySelectorAll<HTMLElement>('p, h1, h2, h3, h4, h5, h6').forEach((block) => {
    const decls = parseDecls(block.getAttribute('style'));
    const picked: Decls = new Map();
    for (const k of CHAR_PROPS) {
      const v = decls.get(k);
      if (v) picked.set(k, v);
      decls.delete(k);
    }
    writeDecls(block, decls);
    if (!picked.size || !block.firstChild) return;
    if (block.querySelector('p, div, table, ul, ol, h1, h2, h3, h4, h5, h6')) return;
    const span = doc.createElement('span');
    writeDecls(span, picked);
    while (block.firstChild) span.appendChild(block.firstChild);
    block.appendChild(span);
  });
}

// ---------------------------------------------------------------------------
// 4. Value normalisation (units the schema + backend sanitizer accept)
// ---------------------------------------------------------------------------

function normalizeBlockDecls(el: HTMLElement) {
  const decls = parseDecls(el.getAttribute('style'));
  if (!decls.size) return;
  expandBoxShorthand(decls, 'margin');

  for (const side of ['margin-top', 'margin-bottom', 'margin-left'] as const) {
    if (!decls.has(side)) continue;
    const pt = toPt(decls.get(side));
    if (pt) decls.set(side, pt);
    else decls.delete(side);
  }
  decls.delete('margin-right');

  if (decls.has('text-indent')) {
    const pt = toPt(decls.get('text-indent'), true);
    if (pt && pt !== '0pt') decls.set('text-indent', pt);
    else decls.delete('text-indent');
  }

  const lh = decls.get('line-height');
  if (lh) {
    const pct = /^(\d*\.?\d+)%$/.exec(lh);
    if (pct) {
      // Unitless keeps the ratio when a span inside uses a bigger font;
      // a % value is resolved once against the paragraph's own font size.
      decls.set('line-height', String(Math.round(parseFloat(pct[1])) / 100));
    } else if (/^\d*\.?\d+$/.test(lh) || lh === 'normal') {
      decls.set('line-height', lh);
    } else {
      const pt = toPt(lh);
      if (pt && pt !== '0pt') decls.set('line-height', pt);
      else decls.delete('line-height');
    }
  }
  writeDecls(el, decls);
}

function normalizeCellDecls(el: HTMLElement) {
  const decls = parseDecls(el.getAttribute('style'));
  if (!decls.size) return;
  const bg = pickColor(decls.get('background-color')) || pickColor(decls.get('background'));
  decls.delete('background');
  if (bg) decls.set('background-color', bg);
  else decls.delete('background-color');

  expandBoxShorthand(decls, 'padding');
  for (const side of ['padding-top', 'padding-right', 'padding-bottom', 'padding-left']) {
    if (!decls.has(side)) continue;
    const pt = toPt(decls.get(side));
    if (pt) decls.set(side, pt);
    else decls.delete(side);
  }
  writeDecls(el, decls);
}

function normalizeOfficeValues(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>('p, h1, h2, h3, h4, h5, h6, li').forEach(normalizeBlockDecls);
  root.querySelectorAll<HTMLElement>('td, th').forEach(normalizeCellDecls);
  root.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
    const decls = parseDecls(el.getAttribute('style'));
    const ff = decls.get('font-family');
    if (!ff) return;
    decls.set('font-family', normalizeFontFamily(ff));
    writeDecls(el, decls);
  });
}

// ---------------------------------------------------------------------------
// 5. Generic Office cleanup
// ---------------------------------------------------------------------------

function cleanStyleString(style: string): string {
  return style
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((prop) => {
      const key = prop.split(':')[0].trim().toLowerCase();
      if (key.startsWith('mso-')) return false;
      if (key.startsWith('page-break-')) return false;
      if (key === 'break-before' || key === 'break-after' || key === 'break-inside') return false;
      if (key === 'tab-stops' || key === 'text-autospace' || key === 'punctuation-wrap') return false;
      return true;
    })
    .join('; ');
}

/** Strip absolute widths and nowrap rules from a single style string so a
 *  pasted table/cell respects the editor's page width instead of forcing
 *  horizontal overflow. */
function stripWidthAndNowrap(style: string): string {
  return style
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((prop) => {
      const key = prop.split(':')[0].trim().toLowerCase();
      if (key === 'width' || key === 'min-width' || key === 'max-width') return false;
      if (key === 'white-space') return false;
      return true;
    })
    .join('; ');
}

/** Word/Excel paste often supplies absolute widths via:
 *    - <table width="..."> / inline `width:` style
 *    - <colgroup><col width="..."> / span+width
 *    - cell <td width="..."> / inline width style / nowrap
 *  Any of these can push the table past the page edge. Drop them all so
 *  the table flows to the available page width with proportional columns.
 *
 *  With `keepColumnWidths` (Office paste) each cell's width is first copied
 *  into Tiptap's own `colwidth` attribute so the table keeps Word's column
 *  proportions; the TableFit extension then scales the columns down if the
 *  table is wider than the page. */
function fitTablesToPage(root: HTMLElement, keepColumnWidths = false) {
  const tables = root.querySelectorAll<HTMLTableElement>('table');
  tables.forEach((tbl) => {
    tbl.removeAttribute('width');
    if (tbl.hasAttribute('style')) {
      const next = stripWidthAndNowrap(tbl.getAttribute('style') || '');
      if (next) tbl.setAttribute('style', next);
      else tbl.removeAttribute('style');
    }

    // Drop <colgroup>: ProseMirror manages column widths via TableMap, and a
    // pasted colgroup with absolute pixel widths is the #1 cause of pasted
    // tables blowing past the page edge.
    tbl.querySelectorAll('colgroup, col').forEach((c) => c.parentNode?.removeChild(c));

    tbl.querySelectorAll<HTMLElement>('td, th').forEach((cell) => {
      if (keepColumnWidths && !cell.hasAttribute('colwidth')) {
        let px = parseFloat(cell.getAttribute('width') || '');
        if (!(px > 0)) {
          const pt = toPt(parseDecls(cell.getAttribute('style')).get('width'));
          px = pt ? (parseFloat(pt) * 4) / 3 : 0;
        }
        const span = Math.max(1, parseInt(cell.getAttribute('colspan') || '1', 10) || 1);
        if (px > 0) {
          const per = Math.max(12, Math.round(px / span));
          cell.setAttribute('colwidth', Array.from({ length: span }, () => per).join(','));
        }
      }
      cell.removeAttribute('width');
      cell.removeAttribute('nowrap');
      if (cell.hasAttribute('style')) {
        const next = stripWidthAndNowrap(cell.getAttribute('style') || '');
        if (next) cell.setAttribute('style', next);
        else cell.removeAttribute('style');
      }
    });
  });
}

function cleanOfficeHTML(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const root = doc.body;

  const listDefs = inlineStyleBlocks(doc, root);
  convertWordLists(root, listDefs);
  highlightToMark(root);
  paragraphFontToSpan(root);
  normalizeOfficeValues(root);
  fitTablesToPage(root, true);

  const all = root.querySelectorAll<HTMLElement>('*');
  all.forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name;
      if (
        name.startsWith('w:') ||
        name.startsWith('o:') ||
        name.startsWith('v:') ||
        name.startsWith('x:') ||
        name.startsWith('xmlns:') ||
        name === 'lang' ||
        name === 'xml:lang'
      ) {
        el.removeAttribute(name);
      }
    });

    if (el.hasAttribute('style')) {
      const cleaned = cleanStyleString(el.getAttribute('style') || '');
      if (cleaned) el.setAttribute('style', cleaned);
      else el.removeAttribute('style');
    }

    if (el.hasAttribute('class')) {
      const kept = (el.getAttribute('class') || '')
        .split(/\s+/)
        .filter((c) => c && !/^(Mso|xl\d)/i.test(c));
      if (kept.length) el.setAttribute('class', kept.join(' '));
      else el.removeAttribute('class');
    }

    const tag = el.tagName;
    if (
      tag === 'O:P' ||
      tag === 'W:PICT' ||
      tag === 'V:SHAPE' ||
      tag === 'V:VML' ||
      tag === 'X:NAME'
    ) {
      while (el.firstChild) el.parentNode?.insertBefore(el.firstChild, el);
      el.parentNode?.removeChild(el);
    }
  });

  // Conditional-comment leftovers (<!--[if !supportLists]--> …).
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  const comments: Node[] = [];
  while (walker.nextNode()) comments.push(walker.currentNode);
  comments.forEach((c) => c.parentNode?.removeChild(c));

  return root.innerHTML;
}
/**
 * Clean clipboard HTML the same way the editor's `transformPastedHTML` hook
 * does. Exported so the ribbon's Paste button (which inserts via
 * `insertContent`, bypassing that hook) produces byte-identical results to a
 * real Ctrl+V — one cleaning path, two entry points.
 */
export function cleanPastedHtml(html: string): string {
  if (!html) return html;
  if (isOfficeHTML(html)) return normalizeImportedDocxHtml(cleanOfficeHTML(html));
  if (!/<table|<ol|<ul/i.test(html)) return html;
  const temp = document.createElement('div');
  temp.innerHTML = html;
  fitTablesToPage(temp);
  return normalizeImportedDocxHtml(temp.innerHTML);
}

const CLOSABLE_FIRST_BLOCKS = new Set(['paragraph', 'heading', 'bulletList', 'orderedList', 'blockquote']);

export const ClipboardClean = Extension.create({
  name: 'clipboardClean',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          transformPastedHTML: (html: string) => {
            // transformPastedHTML is the ONLY place clipboard paste is cleaned
            // (the .docx importers do NOT run for copy-paste). After stripping
            // Office cruft, run the SAME normalization the .docx import path
            // uses so Word copy-paste behaves identically: merge consecutive
            // <ol>/<ul> (numbered lists keep 1,2,3… instead of restarting at 1),
            // strip table `align` (so tables don't float with text wrapping
            // beside them), and collapse runs of empty paragraphs.
            //
            // Keep this body identical to {@link cleanPastedHtml} — the ribbon
            // Paste button calls that so both paste paths agree.
            return cleanPastedHtml(html);
          },
          transformPasted: (slice: Slice, view: EditorView) => {
            // Pasting several paragraphs into an EMPTY paragraph: ProseMirror
            // normally merges the first pasted paragraph into the empty one,
            // so it took the empty paragraph's settings and lost its own
            // alignment / spacing / indent (e.g. a centred Word title came in
            // left-aligned). Closing the slice makes the pasted blocks replace
            // the empty paragraph instead, keeping their formatting.
            const { $from } = view.state.selection;
            const parent = $from.parent;
            const first = slice.content.firstChild;
            if (
              slice.openStart > 0 &&
              parent.isTextblock &&
              parent.content.size === 0 &&
              !parent.type.spec.code &&
              first &&
              CLOSABLE_FIRST_BLOCKS.has(first.type.name) &&
              slice.content.childCount > 0
            ) {
              return new Slice(slice.content, 0, 0);
            }
            return slice;
          }
        }
      })
    ];
  }
});